/**
 * React Query 설정
 * 
 * QueryClient 인스턴스와 기본 설정을 제공합니다.
 * 
 * 사용법:
 * // App.jsx에서
 * import { QueryClientProvider } from '@tanstack/react-query';
 * import { queryClient } from './lib/queryClient';
 * 
 * <QueryClientProvider client={queryClient}>
 *   <App />
 * </QueryClientProvider>
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * 기본 쿼리 설정
 */
const defaultQueryOptions = {
    queries: {
        // 5분 동안 데이터를 신선하게 유지
        staleTime: 5 * 60 * 1000,
        // 10분 후 캐시에서 제거
        cacheTime: 10 * 60 * 1000,
        // 창 포커스 시 자동 리페치 비활성화
        refetchOnWindowFocus: false,
        // 마운트 시 자동 리페치 비활성화
        refetchOnMount: false,
        // 재연결 시 자동 리페치 비활성화
        refetchOnReconnect: false,
        // 3번 재시도
        retry: 3,
        // 재시도 간격 (지수 백오프)
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
        // mutations는 재시도 하지 않음
        retry: 0,
    },
};

/**
 * QueryClient 인스턴스
 */
export const queryClient = new QueryClient({
    defaultOptions: defaultQueryOptions,
});

/**
 * 쿼리 키 팩토리
 * 일관된 쿼리 키 생성을 위한 헬퍼
 */
export const queryKeys = {
    // 예약 관련
    reservations: {
        all: ['reservations'],
        list: (artistId) => [...queryKeys.reservations.all, 'list', artistId],
        detail: (id) => [...queryKeys.reservations.all, 'detail', id],
        byStatus: (artistId, status) =>
            [...queryKeys.reservations.all, 'list', artistId, { status }],
    },

    // 이벤트/캘린더 관련
    events: {
        all: ['events'],
        list: (userId) => [...queryKeys.events.all, 'list', userId],
        range: (userId, start, end) =>
            [...queryKeys.events.all, 'range', userId, start, end],
    },

    // 휴무일 관련
    dayOffs: {
        all: ['dayOffs'],
        byArtist: (artistId) => [...queryKeys.dayOffs.all, 'artist', artistId],
    },

    // 사용자 관련
    users: {
        all: ['users'],
        current: () => [...queryKeys.users.all, 'current'],
        detail: (id) => [...queryKeys.users.all, 'detail', id],
    },

    // 설정 관련
    settings: {
        all: ['settings'],
        calendar: () => [...queryKeys.settings.all, 'calendar'],
    },

    // 메인페이지 공지
    announcements: {
        all: ['announcements'],
        current: () => [...queryKeys.announcements.all, 'current'],
    },

    // 플래시북 관련
    flashbook: {
        all: ['flashbook'],
        planches: {
            all: () => [...queryKeys.flashbook.all, 'planches'],
            detail: (id) => [...queryKeys.flashbook.all, 'planches', id],
        },
        flashes: {
            all: () => [...queryKeys.flashbook.all, 'flashes'],
            byPlanche: (plancheId) => [...queryKeys.flashbook.all, 'flashes', 'planche', plancheId],
            detail: (id) => [...queryKeys.flashbook.all, 'flashes', id],
        },
    },
};

export default queryClient;
